import type { DirectoryNode, FileNode, FileSystemTree } from '@webcontainer/api'
import type { FlatRoute, ProjectMessage } from '@/lib/types'
import { cloneDeep } from 'lodash'
import { sha256 } from './utils'
import { extractRoutes } from './ast'

export const FILE_SYSTEM_TREE_NOT_FOUND = -1

export type FileSystemTreeDiff = {
  type: 'create' | 'update' | 'remove'
  path: string
  contents?: string
}[]

export const applyFileSystemTreeDiff = (
  fileSystemTree: FileSystemTree,
  fileSystemTreeDiff: FileSystemTreeDiff,
) => {
  const newfileSystemTree = cloneDeep(fileSystemTree)

  for (const diff of fileSystemTreeDiff) {
    const pathParts = diff.path.split('/')
    const fileName = pathParts.pop()!

    let currentNode = newfileSystemTree
    for (const part of pathParts) {
      if (!currentNode[part]) {
        currentNode[part] = { directory: {} }
      }
      currentNode = (currentNode[part] as DirectoryNode).directory
    }

    switch (diff.type) {
      case 'create':
      case 'update':
        if (!diff.contents) {
          console.error('Contents required for create/update')
          break
        }
        currentNode[fileName] = { file: { contents: diff.contents } }
        break
      case 'remove':
        delete currentNode[fileName]
        break
    }
  }

  return newfileSystemTree
}

export const injectExtraScriptsToIndexHtml = (
  fileSystemTree: FileSystemTree,
  extraScripts: string[],
) => {
  const injectedFileSystemTree = cloneDeep(fileSystemTree)
  const indexHtml = (
    injectedFileSystemTree['index.html'] as FileNode | undefined
  )?.file.contents as string
  if (!indexHtml) return injectedFileSystemTree

  const newIndexHtml = indexHtml.replace(
    '</body>',
    `${extraScripts.join('\n')}\n</body>`,
  )

  injectedFileSystemTree['index.html'] = {
    file: {
      contents: newIndexHtml,
    },
  }
  return injectedFileSystemTree
}

export const replaceInFileSystemTree = (
  fileSystemTree: FileSystemTree,
  oldContent: string,
  newContent: string,
) => {
  const replacedFileSystemTree = cloneDeep(fileSystemTree)

  const replaceInNode = (node: FileSystemTree): FileSystemTree => {
    const newNode: FileSystemTree = {}

    for (const [fileName, content] of Object.entries(node)) {
      // Replace project ID in filename
      const newFileName = fileName.replaceAll(oldContent, newContent)

      if (
        'file' in content &&
        'contents' in content.file &&
        typeof content.file.contents === 'string'
      ) {
        // Replace project ID in file contents
        newNode[newFileName] = {
          file: {
            contents: content.file.contents.replaceAll(oldContent, newContent),
          },
        }
      } else if ('directory' in content) {
        // Recursively replace in directory
        newNode[newFileName] = {
          directory: replaceInNode(content.directory),
        }
      } else {
        newNode[newFileName] = content
      }
    }

    return newNode
  }

  return replaceInNode(replacedFileSystemTree)
}

export const getAppRoutes = (fileSystemTree: FileSystemTree) => {
  const code =
    getFileContentsByPath(fileSystemTree, 'src/App.jsx') ||
    getFileContentsByPath(fileSystemTree, 'src/app.jsx')
  if (!code) return []
  const routes = extractRoutes(code)
  return routes
    .map((route) => ({ path: route.path, element: route.element }) as FlatRoute)
    .filter((route) => !route.path.includes('/:') && !route.path.includes('*'))
}

export const hasSrcDataDirectory = (tree: FileSystemTree) => {
  if (!tree) return false
  return (
    'src' in tree &&
    'data' in (tree.src as DirectoryNode).directory &&
    ((tree.src as DirectoryNode).directory.data as DirectoryNode).directory
  )
}

export const hashFileSystemTree = async (fileSystemTree: FileSystemTree) => {
  return await sha256(JSON.stringify(fileSystemTree))
}

export const getFileContentsByPath = (tree: FileSystemTree, path: string) => {
  let fst = tree
  const parts = path.split('/')
  for (const part of parts) {
    if (fst && part in fst) {
      if (fst[part] && 'file' in fst[part]) {
        return (fst[part].file as { contents: string }).contents
      }
      if (fst[part] && 'directory' in fst[part]) {
        fst = fst[part].directory as FileSystemTree
      }
    } else {
      return ''
    }
  }
  return ''
}

export const setFileContentsByPath = (
  tree: FileSystemTree,
  path: string,
  code: string,
) => {
  let fst = tree
  const parts = path.split('/')
  for (const part of parts.slice(0, -1)) {
    if (!fst[part]) {
      fst[part] = { directory: {} }
    }
    fst = (fst[part] as DirectoryNode).directory as FileSystemTree
  }
  if (parts.length) {
    fst[parts.pop()!] = { file: { contents: code } }
  }
  return tree
}

export const isHeybossTemplateProject = (fileSystemTree: FileSystemTree) => {
  if (!fileSystemTree) return false
  return (
    'src' in fileSystemTree &&
    'directory' in fileSystemTree.src &&
    'App.jsx' in fileSystemTree.src.directory &&
    'file' in fileSystemTree.src.directory['App.jsx'] &&
    (
      fileSystemTree.src.directory['App.jsx'].file as { contents: string }
    ).contents.includes('Heyboss Template</div>')
  )
}

export const mapUserMessageIdToFileSystemTreeIndex = (
  messages: ProjectMessage[],
  treelist: FileSystemTree[],
  offset = 0,
  remix = false,
) => {
  const result: Record<string, number> = {}
  let index = isHeybossTemplateProject(treelist[0]) ? 1 : 0
  let isFirstUserMessage = true

  for (let i = 0; i < messages.length; i++) {
    const message = messages[i]

    if (isFirstUserMessage && message.role === 'user') {
      isFirstUserMessage = false
      result[message.id] = index
      index += 1
      continue
    }

    if (message.role !== 'user') {
      continue
    }

    if (remix && i === 0 && message.role === 'user') {
      result[message.id] = index++
      continue
    }

    if (message.type === 'editor') {
      result[message.id] = message.metadata?.file_system_tree_index! - offset
      index += 1
      continue
    }

    if (i === messages.length - 1 && messages[i].role === 'user') {
      result[message.id] = treelist[index] ? index : FILE_SYSTEM_TREE_NOT_FOUND
      break
    }

    let isFixingIssue = message.contentForAi
      ?.toLocaleLowerCase()
      .includes('fixing issue')

    let isAiHadABug = false
    let isUseTemplate = false
    let hasSystemArtifact = false
    let allActionsSuccess = false
    let isArtifactComplete = false

    for (let j = i + 1; j < messages.length; j++) {
      const nextmsg = messages[j]
      if (nextmsg.role === 'user') {
        if (
          nextmsg.contentForAi?.toLocaleLowerCase().includes('fixing issue')
        ) {
          isFixingIssue = true
        } else if (
          !hasSystemArtifact ||
          !allActionsSuccess ||
          !isArtifactComplete
        ) {
          result[message.id] = FILE_SYSTEM_TREE_NOT_FOUND
          break
        }
      } else if (nextmsg.role === 'system' && nextmsg.type === 'artifact') {
        hasSystemArtifact = true
        allActionsSuccess = Boolean(
          nextmsg.artifact?.actions.every(
            (action) => action.status === 'success',
          ),
        )
      } else if (
        nextmsg.role === 'assistant' &&
        nextmsg.content?.toLowerCase().includes('ai had a bug')
      ) {
        isAiHadABug = true
      } else if (
        nextmsg.role === 'assistant' &&
        nextmsg.type === 'artifact_complete'
      ) {
        isArtifactComplete = true
      } else if (
        nextmsg.role === 'assistant' &&
        nextmsg.content?.startsWith('Use template')
      ) {
        isUseTemplate = true
      }

      if (
        isAiHadABug ||
        isUseTemplate ||
        (isFixingIssue && isArtifactComplete) ||
        (hasSystemArtifact && allActionsSuccess) /* && isArtifactComplete */
      ) {
        result[message.id] = treelist[index]
          ? index
          : FILE_SYSTEM_TREE_NOT_FOUND
        index += treelist[index] ? 1 : 0
        i = j
        break
      }
      i = j
    }
  }
  return result
}

export const mergeFileSystemTrees = (
  baseTree: FileSystemTree,
  mergeTree: FileSystemTree,
) => {
  const merged: FileSystemTree = cloneDeep(baseTree)

  for (const [path, node] of Object.entries(mergeTree)) {
    if ('file' in node) {
      // It's a file node, replace directly
      merged[path] = node
    } else if ('directory' in node) {
      // It's a directory node
      if (merged[path] && 'directory' in merged[path]) {
        // Merge with existing directory
        merged[path] = {
          directory: mergeFileSystemTrees(
            (merged[path] as DirectoryNode).directory,
            node.directory,
          ),
        }
      } else {
        // Create new directory or replace existing non-directory
        merged[path] = node
      }
    }
  }

  return merged
}

export const getFileSystemProjectConfig = (fileSystemTree: FileSystemTree) => {
  const projectConfig = JSON.parse(
    (
      fileSystemTree['project_config.json'] as FileNode
    )?.file.contents.toString() || '{}',
  )
  return projectConfig
}

export const getFileSystemMigrationIndex = (fileSystemTree: FileSystemTree) => {
  const formatMigrationIndex = (index: number) => {
    return String(index).padStart(3, '0')
  }

  let migrationIndex = 1
  if (!fileSystemTree['supabase']) {
    return formatMigrationIndex(migrationIndex)
  }
  if (!(fileSystemTree['supabase'] as DirectoryNode)?.directory['migrations']) {
    return formatMigrationIndex(migrationIndex)
  }
  const migrations = (fileSystemTree['supabase'] as DirectoryNode).directory[
    'migrations'
  ] as DirectoryNode
  Object.entries(migrations.directory).forEach(([key]) => {
    if (key.endsWith('.sql')) {
      const match = key.match(/(\d{3})_(.*)\.sql/)
      if (match) {
        const [, index] = match
        migrationIndex = Math.max(migrationIndex, Number(index) + 1)
      }
    }
  })
  return formatMigrationIndex(migrationIndex)
}

export const fileSystemTreeToFiles = (fileSystemTree: FileSystemTree) => {
  const files: Record<string, string> = {}

  const traverseTree = (tree: FileSystemTree, currentPath: string = '') => {
    for (const [name, node] of Object.entries(tree)) {
      const fullPath = currentPath ? `${currentPath}/${name}` : name

      if ('file' in node && 'contents' in node.file) {
        files[fullPath] = node.file.contents.toString()
      } else if ('directory' in node) {
        traverseTree(node.directory, fullPath)
      }
    }
  }

  traverseTree(fileSystemTree)
  return files
}

export const getFileSystemTreeSchemas = (fileSystemTree: FileSystemTree) => {
  const files = fileSystemTreeToFiles(fileSystemTree)
  const schemaReg = /src\/shared\/schemas\/(.+)\.json/

  return Object.entries(files)
    .filter(([path]) => schemaReg.test(path))
    .map(([, contents]) => {
      return JSON.parse(contents)
    })
}
